<template>
  <v-container fluid>
    <v-col class="pt-0 mt-0" cols="12">
      <v-row justify="start" align="start">
        <v-switch
          class="switch pt-0 mt-0"
          v-model="settings.schedulerSwitch"
          hide-details
          :label="`Environment scheduler ${
            settings.schedulerSwitch ? 'enabled' : 'disabled'
          }`"
          :disabled="
            details.Status.toUpperCase() === 'DURING_RESTORE' ||
            details.TemplateStatus.toUpperCase() === 'PENDING' ||
            details.SnapshotStatus.toUpperCase() === 'PENDING'
          "
        ></v-switch>
        <v-spacer></v-spacer>
        <div class="mr-6" v-if="showEditIcons && !schedulerLoading">
          <v-icon
            :disabled="
              $v.$invalid ||
              details.Status.toUpperCase() === 'DURING_RESTORE' ||
              details.TemplateStatus.toUpperCase() === 'PENDING' ||
              details.SnapshotStatus.toUpperCase() === 'PENDING'
            "
            class="mr-5 icons"
            @click="onConfirm"
            >fal fa-check</v-icon
          >
          <v-icon class="icons" @click="onDiscard">fal fa-times</v-icon>
        </div>
        <v-icon v-if="schedulerLoading" class="icons"
          >fas fa-circle-notch fa-spin</v-icon
        >
      </v-row>
    </v-col>
    <v-card id="scheduler" v-show="settings.schedulerSwitch">
      <v-col class="d-flex pb-0 pr-5 pl-5" cols="12" sm="12">
        <v-select
          :menu-props="{ offsetY: true }"
          :items="timezones"
          item-text="name"
          item-value="value"
          v-model="settings.selectedTimezone"
          label="Timezone"
          append-icon="fas fa-caret-down"
        ></v-select>
      </v-col>
      <v-stepper non-linear>
        <template>
          <v-stepper-header>
            <template v-for="step in steps">
              <v-stepper-step
                class="stepper-step"
                :edit-icon="null"
                :key="`${step.id}-step`"
                :complete="checkIfSelected(step.label)"
                :step="step.label"
                :editable="true"
                :color="
                  errors[0] && !errors[1].day.required ? 'red' : step.color
                "
                @click="addDays(step.label)"
              ></v-stepper-step>

              <v-divider
                v-if="step.id !== steps.length"
                :key="step.id"
                :color="
                  steps[step.id].color === '#6DB42C' &&
                  steps[step.id - 1].color !== 'rgba(0, 0, 0, 0.38)'
                    ? '#6DB42C'
                    : 'grey'
                "
                class="divider"
              ></v-divider>
            </template>
          </v-stepper-header>
        </template>
      </v-stepper>
      <v-col class="d-flex pt-0" cols="12" sm="12">
        <v-col class="d-flex pt-0 ml-2" cols="12" sm="12">
          <p class="select-hour-labels">From</p>
          <v-text-field
            class="select-hour-text-fields"
            v-model.number="settings.startHour"
            oninput="this.value=this.value.slice(0,this.maxLength)"
            maxlength="2"
            type="number"
            min="1"
            max="12"
            :error="errors[0] && !startHour"
          ></v-text-field>
          <v-select
            :menu-props="{ offsetY: true }"
            class="select-hour"
            v-model="settings.hourPostfixFrom"
            :items="hourPostfix"
            :item-text="hours.text"
            append-icon="fas fa-caret-down"
            :disabled="!settings.selectedDays"
          ></v-select>
          <p class="select-hour-labels ml-4">To</p>
          <v-text-field
            class="select-hour-text-fields"
            v-model.number="settings.stopHour"
            oninput="this.value=this.value.slice(0,this.maxLength)"
            maxlength="2"
            type="number"
            min="1"
            max="12"
            :error="errors[0] && !stopHour"
          ></v-text-field>
          <v-select
            :menu-props="{ offsetY: true }"
            class="select-hour"
            v-model="settings.hourPostfixTo"
            :items="hourPostfix"
            :item-text="hours.text"
            append-icon="fas fa-caret-down"
            :disabled="!settings.selectedDays"
          ></v-select>
        </v-col>
      </v-col>
    </v-card>
  </v-container>
</template>

<script>
import { validationMixin } from "vuelidate";
import { required, requiredIf, maxLength } from "vuelidate/lib/validators";
import { mapActions } from "vuex";

export default {
  data() {
    return {
      steps: [
        { id: 1, label: "Mn", color: "rgba(0, 0, 0, 0.38)" },
        { id: 2, label: "Tu", color: "rgba(0, 0, 0, 0.38)" },
        { id: 3, label: "We", color: "rgba(0, 0, 0, 0.38)" },
        { id: 4, label: "Th", color: "rgba(0, 0, 0, 0.38)" },
        { id: 5, label: "Fd", color: "rgba(0, 0, 0, 0.38)" },
        { id: 6, label: "Sd", color: "rgba(0, 0, 0, 0.38)" },
        { id: 7, label: "Su", color: "rgba(0, 0, 0, 0.38)" },
      ],
      timezones: [
        { name: "(GMT -12:00) Eniwetok, Kwajalein", value: "-12" },
        { name: "(GMT -11:00) Midway Island, Samoa", value: "-11" },
        { name: "(GMT -10:00) Hawaii", value: "-10" },
        { name: "(GMT -9:00) Alaska", value: "-9" },
        { name: "(GMT -8:00) Pacific Time (US Canada)", value: "-8" },
        { name: "(GMT -7:00) Mountain Time (US Canada)", value: "-7" },
        {
          name: "(GMT -6:00) Central Time (US Canada), Mexico City",
          value: "-6",
        },
        {
          name: "(GMT -5:00) Eastern Time (US Canada), Bogota, Lima",
          value: "-5",
        },
        {
          name: "(GMT -4:00) Atlantic Time (Canada), Caracas, La Paz",
          value: "-4",
        },
        { name: "(GMT -3:00) Brazil, Buenos Aires, Georgetown", value: "-3" },
        { name: "(GMT -2:00) Mid-Atlantic", value: "-2" },
        { name: "(GMT -1:00) Azores, Cape Verde Islands", value: "-1" },
        {
          name: "(GMT) Western Europe Time, London, Lisbon, Casablanca",
          value: "+0",
        },
        {
          name: "(GMT +1:00) Brussels, Copenhagen, Madrid, Paris",
          value: "+1",
        },
        { name: "(GMT +2:00) Kaliningrad, South Africa", value: "+2" },
        {
          name: "(GMT +3:00) Baghdad, Riyadh, Moscow, St. Petersburg",
          value: "+3",
        },
        { name: "(GMT +4:00) Abu Dhabi, Muscat, Baku, Tbilisi", value: "+4" },
        {
          name: "(GMT +5:00) Ekaterinburg, Islamabad, Karachi, Tashkent",
          value: "+5",
        },
        { name: "(GMT +6:00) Almaty, Dhaka, Colombo", value: "+6" },
        { name: "(GMT +7:00) Bangkok, Hanoi, Jakarta", value: "+7" },
        {
          name: "(GMT +8:00) Beijing, Perth, Singapore, Hong Kong",
          value: "+8",
        },
        {
          name: "(GMT +9:00) Tokyo, Seoul, Osaka, Sapporo, Yakutsk",
          value: "+9",
        },
        {
          name: "(GMT +10:00) Eastern Australia, Guam, Vladivostok",
          value: "+10",
        },
        {
          name: "(GMT +11:00) Magadan, Solomon Islands, New Caledonia",
          value: "+11",
        },
        {
          name: "(GMT +12:00) Auckland, Wellington, Fiji, Kamchatka",
          value: "+12",
        },
        { name: "(GMT +13:00) Apia, Nukualofa", value: "+13" },
        { name: "(GMT +14:00) Line Islands, Tokelau", value: "+14" },
      ],
      settings: {
        startHour: "",
        stopHour: "",
        selectedDays: [],
        hourPostfixFrom: "AM",
        hourPostfixTo: "AM",
        schedulerSwitch: this.details.SchedulerEnabled,
        selectedTimezone: "",
      },
      hours: [],
      scheduler: [],
      currentlySelectedDays: [],
      hourPostfix: ["AM", "PM"],
      showEditIcons: false,
      currentSettings: {},
    };
  },
  props: ["errors", "details", "schedulerLoading"],
  mixins: [validationMixin],
  validations: {
    settings: {
      selectedTimezone: {
        required: requiredIf(function () {
          return this.settings.schedulerSwitch;
        }),
      },
      startHour: {
        required: requiredIf(function () {
          return this.settings.schedulerSwitch;
        }),
        validateHours() {
          if(Number.parseInt(this.settings.startHour) === 0)
            return false;
          const regex = /^([0-9]|1[0-9]|2[0-3])$/;
          const startHour = this.convertTo24hTime(
            this.settings.startHour,
            this.settings.hourPostfixFrom
          );
          const stopHour = this.convertTo24hTime(
            this.settings.stopHour,
            this.settings.hourPostfixTo
          );
          return this.settings.schedulerSwitch
            ? startHour < stopHour &&
                startHour.toString().match(regex) &&
                stopHour.toString().match(regex)
            : true;
        },
      },
      stopHour: {
        required: requiredIf(function () {
          return this.settings.schedulerSwitch;
        }),
        validateHours() {
           if(Number.parseInt(this.settings.stopHour) === 0)
            return false;
          const regex = /^([0-9]|1[0-9]|2[0-3])$/;
          const startHour = this.convertTo24hTime(
            this.settings.startHour,
            this.settings.hourPostfixFrom
          );
          const stopHour = this.convertTo24hTime(
            this.settings.stopHour,
            this.settings.hourPostfixTo
          );
          return this.settings.schedulerSwitch
            ? startHour < stopHour &&
                startHour.toString().match(regex) &&
                stopHour.toString().match(regex)
            : true;
        },
      },
      selectedDays: {
        validateDays() {
          return this.settings.selectedDays.length > 0;
        },
      },
    },
  },
  methods: {
    ...mapActions(["calculateAmount", "setSchedulerStatus"]),
    addDays(label) {
      this.showEditIcons = true;
      if (this.settings.selectedDays.includes(label)) {
        const result = this.settings.selectedDays.filter((day) => day != label);

        this.steps.map((step) => {
          if (step.label == label) {
            step.color = "rgba(0, 0, 0, 0.38)";
          }
        });

        this.settings.selectedDays = result;
      } else {
        this.steps.map((step) => {
          if (step.label == label) {
            for (let i = step.id; i >= 1; i--) {
              if (this.steps[i - 1].label === label) {
                this.settings.selectedDays.push(this.steps[i - 1].label);
                this.steps[i - 1].color = "#6DB42C";

                document.querySelectorAll(".v-stepper__step__step")[
                  i - 1
                ].innerHTML = this.steps[i - 1].label;
              }
            }
          }
        });
        this.settings.selectedDays = [...new Set(this.settings.selectedDays)];
      }
    },
    checkIfSelected(label) {
      return this.settings.selectedDays.includes(label) ? true : false;
    },
    convertTime(hour) {
      if (!hour)
        return {
          hour: "",
          postfix: "AM",
        };
      hour = parseInt(hour, 10);
      let h = hour % 12;
      if (h === 0) h = 12;
      const postfix = hour < 12 ? "AM" : "PM";
      return {
        hour: h,
        postfix: postfix,
      };
    },
    convertTo24hTime(hour, postfix) {
      const parsedHour = parseInt(hour, 10);
      let convertedHour = "";
      if (postfix === "PM" && parsedHour >= 1 && parsedHour <= 12) {
        if (parsedHour === 12) convertedHour = parsedHour;
        else convertedHour = 12 + parsedHour;
      } else if (postfix === "AM" && parsedHour >= 1 && parsedHour <= 12) {
        if (parsedHour === 12) convertedHour = 0;
        else convertedHour = parsedHour;
      }
      return convertedHour;
    },
    onConfirm() {
      const settings = {};
      Object.assign(settings, this.settings);
      settings.startHour = this.convertTo24hTime(
        this.settings.startHour,
        this.settings.hourPostfixFrom
      );
      settings.stopHour = this.convertTo24hTime(
        this.settings.stopHour,
        this.settings.hourPostfixTo
      );
      settings.selectedDays = Array.from(this.settings.selectedDays).map(
        (day) => this.steps.find((step) => step.label === day).id
      );
      this.$emit("schedulerModificationConfirm", settings);
      this.showEditIcons = false;
    },
    onDiscard() {
      this.applySchedulerSettings();
      this.showEditIcons = false;
    },
    applySchedulerSettings() {
      this.clearSchedulerSettings();
      this.settings.schedulerSwitch = this.details.SchedulerEnabled;
      this.currentlySelectedDays = this.details.SchedulerDays.split(
        ","
      ).map((item) => (parseInt(item, 10) === 0 ? 7 : parseInt(item, 10)));
      this.steps.forEach((day) => {
        this.currentlySelectedDays.forEach((dayNumber) => {
          if (day.id === dayNumber) this.addDays(day.label);
        });
      });
      console.log("currentlySelectedDays", this.currentlySelectedDays);
      this.settings.selectedTimezone = this.details.TimezoneOffset;
      const from = this.convertTime(this.details.StartHour);
      const to = this.convertTime(this.details.StopHour);
      this.settings.startHour = from.hour;
      this.settings.hourPostfixFrom = from.postfix;
      this.settings.stopHour = to.hour;
      this.settings.hourPostfixTo = to.postfix;
      Object.assign(this.currentSettings, this.settings);
      this.currentSettings.selectedDays = Array.from(
        this.settings.selectedDays
      );
    },
    clearSchedulerSettings() {
      this.settings.startHour = "";
      this.settings.stopHour = "";
      this.settings.selectedDays = [];
      this.scheduler = [];
      this.settings.selectedTimezone = "";
      this.steps.map((step) => (step.color = "rgba(0, 0, 0, 0.38)"));
    },
  },
  mounted() {
    this.applySchedulerSettings();
    this.showEditIcons = false;
  },
  watch: {
    settings: {
      deep: true,
      handler() {
        this.showEditIcons = !Object.keys(this.currentSettings).every(
          (key) =>
            this.settings.hasOwnProperty(key) &&
            (key !== "selectedDays"
              ? this.settings[key] === this.currentSettings[key]
              : this.settings.selectedDays.length ===
                  this.currentSettings.selectedDays.length &&
                this.settings.selectedDays.every((day) =>
                  this.currentSettings.selectedDays.some(
                    (currDay) => day === currDay
                  )
                ))
        );

        const startHour = this.convertTo24hTime(
          this.settings.startHour,
          this.settings.hourPostfixFrom
        );
        const stopHour = this.convertTo24hTime(
          this.settings.stopHour,
          this.settings.hourPostfixTo
        );

        const selectedDays = Array.from(this.settings.selectedDays)
          .map((day) => {
            const id = this.steps.find((step) => step.label === day).id;
            return id === 7 ? 0 : id;
          })
          .join(",");

        const data = {
          stopHour,
          startHour,
          schedulerDays: selectedDays,
          isEnabled: this.settings.schedulerSwitch,
        };

        if (this.details.ConnectedId) {
          data.prices = this.details.environments.map((env) => env.Price);
        } else {
          const { Price } = this.details;
          data.prices = [Price];
        }

        this.setSchedulerStatus(data);
        this.calculateAmount();
      },
    },
    details: {
      deep: true,
      handler() {
        this.applySchedulerSettings();
      },
    },
  },
};
</script>

<style scoped>
#scheduler {
  border: 0;
  box-shadow: none;
  border-radius: 0;
}

.v-stepper {
  box-shadow: none !important;
  border-radius: 0;
  height: 9vh !important;
}

.v-stepper__header {
  height: 9vh !important;
}

.v-stepper__header:hover {
  box-shadow: none !important;
}

.v-stepper__header,
.v-card,
.v-sheet {
  background: #f9f9f9;
}

.select-hour-labels {
  margin-top: 2.2vh;
  margin-right: 1vw;
  font: medium 1.6vh/1.9vh "Montserrat" !important;
}

.select-hour-text-fields >>> input {
  font: bold 1.6vh/1.9vh "Montserrat";
  text-align: center;
  width: 3vw;
}

.select-hour {
  margin-right: 1.2vw;
  margin-left: 1vw;
}

.stepper-step >>> .v-stepper__step__step {
  width: 2vw;
  height: 4vh;
}

.switch {
  text-decoration: none !important;
}

.icons {
  color: #1972d2;
}
</style>